import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Popper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, createAuthHeader } from '../../../config/api.config';
import { colors } from '../../../themes/typography';
import { REPLACEMENT_TEST_WEEKS, startsTooSoonForErp } from '../../../constants/experiments';
import { getApiErrorMessage } from '../../../utils/apiError';
import { shortName } from '../../../utils/productShortName';
import { fetchCandidateSuggestion } from '../../ExperimentManagement/services/experimentManagementApi';
import MonthlyUnitsBars, { hasAbsentMonth } from '../../../components/POSAnalysis/MonthlyUnitsBars';
import { alternativeOptions } from '../../../components/shared/ReplacementAlternatives';
import { IncomingProductAutocomplete, ExperimentStartFields } from '../../../components/shared/IncomingProductPicker';
import ExperimentSuggestion from './ExperimentSuggestion';
import LaneCandidateList, { useLaneCandidates } from './LaneCandidateList';

const HISTORY_MONTHS = 6;
// The floating lane card's frame: the dark of the Create button, nothing like the heat map's oranges
const CARD_FRAME = '#2f2a3d';
const PANEL_WIDTH = 320;

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// 1st of next month — the one after when that 1st is closer than the ERP allows
const defaultStart = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1, 1);
  d.setHours(0, 0, 0, 0);
  if (startsTooSoonForErp(d)) d.setMonth(d.getMonth() + 1, 1);
  return d;
};

// The catalogue for a product not on the planogram, loaded once per client for the session
const catalogue = new Map();
const loadCatalogue = (clientId) => {
  if (!catalogue.has(clientId)) {
    const request = axios.get(`${API_BASE_URL}/all-products?clientId=${encodeURIComponent(clientId)}`,
      { headers: createAuthHeader(localStorage.getItem('access_token')) })
      .then((resp) => resp.data?.products || [])
      .catch(() => { catalogue.delete(clientId); return []; });
    catalogue.set(clientId, request);
  }
  return catalogue.get(clientId);
};

const optionsOf = (s) => alternativeOptions(s).map((o) => ({ ...o, id: o.productId, name: o.productName }));

/**
 * The Machines planogram's experiment mode, docked at its right: the lanes clicked on the planogram (the queue),
 * grouped by product, and, floating over the lane under the pointer, what its product sold from that lane over the
 * last months. "Elegir reemplazo" turns the next click on the planogram into the
 * product coming in — one for the whole queue, the orchestrator's recommendation to start with, its products starred
 * on the grid, the lane under the pointer previewed in the picker (`previewIncoming`) — or any product searched here. "Crear" makes one accepted replacement per product going out, on its
 * queued lanes. The recommendations go up through `onRecommendations` so the grid can star them. A suggestion lane
 * clicked (`suggestion`) takes the panel until it is accepted, rejected or closed; the queue waits behind it.
 */
const ExperimentModePanel = ({
  clientId, machine, lanes, pointer, busyExperiment, picking, onStartPicking, onBack, incoming, previewIncoming, onIncomingChange, onRemoveLanes,
  onRecommendations, onCreated, suggestion, onCloseSuggestion, onSuggestionResolved,
}) => {
  const { t, i18n } = useTranslation();
  const locale = { es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES';
  const machineId = Number(machine.machineId);
  const [history, setHistory] = useState(null);
  const [allProducts, setAllProducts] = useState(null);
  const [suggestions, setSuggestions] = useState({}); // d_product_id -> { margin, volume } (absent = loading)
  const [startDate, setStartDate] = useState(defaultStart);
  const [weeks, setWeeks] = useState(REPLACEMENT_TEST_WEEKS);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setHistory(null);
    axios.get(`${API_BASE_URL}/metrics/store/${machineId}/planogram?clientId=${encodeURIComponent(clientId)}&months=${HISTORY_MONTHS}&lanes=1`,
      { headers: createAuthHeader(localStorage.getItem('access_token')) })
      .then(({ data }) => { if (!cancelled && data.monthly?.lanes) setHistory(data.monthly); })
      .catch(() => {});
    loadCatalogue(clientId).then((products) => { if (!cancelled) setAllProducts(products); });
    return () => { cancelled = true; };
  }, [clientId, machineId]);
  const scaleMax = useMemo(
    () => (history ? Math.max(1, ...Object.values(history.lanes).flatMap((lane) => lane.units)) : 1),
    [history]
  );

  // The queue by product going out, in the order its first lane was clicked
  const groups = useMemo(() => {
    const byProduct = new Map();
    lanes.forEach((s) => {
      const id = Number(s.d_product_id);
      if (!byProduct.has(id)) byProduct.set(id, { productId: id, name: s.name, slots: [] });
      byProduct.get(id).slots.push(s);
    });
    return [...byProduct.values()];
  }, [lanes]);

  // The orchestrator's suggestion for each product going out (the "Sugerencia IA" call), asked once per product
  useEffect(() => {
    let cancelled = false;
    groups.filter((g) => !(g.productId in suggestions)).forEach(({ productId }) => {
      fetchCandidateSuggestion(clientId, machineId, productId, 'reemplazo_producto').then((s) => {
        if (!cancelled) setSuggestions((prev) => ({ ...prev, [productId]: { margin: s?.candidates || [], volume: s?.volumeCandidates || [] } }));
      });
    });
    return () => { cancelled = true; };
  }, [groups, clientId, machineId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The products worth putting in the queued lanes, asked again only when the queue changes
  const outgoingKey = groups.map((g) => `${g.productId}:${g.slots.length}`).join(',');
  const { candidates: laneCandidates, originLaneCapacities } = useLaneCandidates(clientId, machineId, outgoingKey, picking);

  // One list of recommendations for the queue: each product's options, the first one wins
  const recommended = useMemo(() => {
    const seen = new Map();
    groups.forEach((g) => optionsOf(suggestions[g.productId] || {}).forEach((o) => { if (!seen.has(o.id)) seen.set(o.id, o); }));
    groups.forEach((g) => seen.delete(g.productId));
    return [...seen.values()];
  }, [groups, suggestions]);
  // Only when they change: the page re-renders on every report
  const recommendedKey = recommended.map((o) => o.id).join(',');
  useEffect(() => { onRecommendations(recommended); }, [recommendedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadingSuggestions = groups.some((g) => !(g.productId in suggestions));

  // Picking starts on the recommendation
  useEffect(() => {
    if (picking && !incoming && recommended[0]) onIncomingChange({ option: recommended[0], laneKey: null });
  }, [picking, recommendedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const endDate = useMemo(() => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + weeks * 7);
    return d;
  }, [startDate, weeks]);

  const create = async () => {
    setCreating(true);
    setMessage(null);
    const option = incoming.option;
    const created = [];
    const failed = [];
    for (const g of groups) {
      if (g.productId === Number(option.id)) continue;
      try {
        const response = await axios.post(`${API_BASE_URL}/experiments/create?clientId=${encodeURIComponent(clientId)}`, {
          dStoreId: machineId,
          dProductId: g.productId,
          newDProductId: Number(option.id),
          experimentType: 'reemplazo_producto',
          dateFrom: fmtDate(startDate),
          dateTo: fmtDate(endDate),
          status: 'accepted',
          selectionIds: g.slots.map((s) => String(s.selection_id)),
          tags: [],
          // From the orchestrator's suggestion or the best products for these lanes, not a product picked by hand
          isAi: recommended.some((o) => o.id === option.id)
            || (laneCandidates || []).some((c) => c.productId === Number(option.id)),
          innovation: false,
        }, { headers: createAuthHeader(localStorage.getItem('access_token')) });
        g.slots.forEach((s) => created.push({ ...s, _experimentResponse: response.data }));
      } catch (error) {
        failed.push(`${shortName(g.name)}: ${getApiErrorMessage(error)}`);
      }
    }
    setCreating(false);
    if (failed.length) setMessage(failed.join(' · '));
    if (created.length) onCreated(created);
  };

  const hovered = pointer?.slot;
  const fmtDay = (d) => new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  // The products going out, by name, for the lane size they load
  const originName = groups.map((g) => shortName(g.name)).join(' · ');
  const hoveredLane = hovered ? history?.lanes[`${hovered.selection_id}:${hovered.d_product_id}`] : null;
  const hoveredUnits = hoveredLane?.units ?? history?.months.map(() => 0);
  const hoveredDays = hoveredLane?.days ?? history?.months.map(() => 0);
  const laneCount = lanes.length;

  return (
    <Box sx={{
      width: PANEL_WIDTH, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 8,
      p: 1.5, borderRadius: '10px', backgroundColor: colors.background.surface,
      border: `1px solid ${colors.border.light}`,
    }}>
      {/* The lane under the pointer: its months right over it, so the eyes stay on the planogram */}
      <Popper
        open={Boolean(hovered && (history || busyExperiment))}
        anchorEl={pointer?.el}
        placement="bottom"
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
        sx={{ zIndex: 1300, pointerEvents: 'none' }}
      >
        {/* A dark frame and a deep shadow, so the card does not melt into the orange heat map under it */}
        <Box sx={{
          p: '5px 7px 3px', borderRadius: '8px', backgroundColor: colors.background.surface,
          border: `2px solid ${CARD_FRAME}`, boxShadow: '0 8px 24px rgba(0, 0, 0, 0.28)',
        }}>
          {/* A lane clicked that already has an experiment: that one, instead of queueing it */}
          {busyExperiment && (
            <Box sx={{ maxWidth: 230, mb: history ? 0.5 : 0.25 }}>
              <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: '#b45309' }}>
                {t('exp_laneBusy', { status: busyExperiment.status === 'manual_review' ? t('machines_aiSuggestion') : t(busyExperiment.status) })}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: colors.text.primary }}>
                {busyExperiment.originalProduct?.name || hovered?.name}
                {busyExperiment.candidateNames?.[0] && busyExperiment.candidateNames[0] !== busyExperiment.originalProduct?.name
                  ? ` → ${busyExperiment.candidateNames[0]}` : ''}
              </Typography>
              {busyExperiment.dateFrom && busyExperiment.dateTo && (
                <Typography sx={{ fontSize: '10.5px', color: colors.text.secondary }}>
                  {fmtDay(busyExperiment.dateFrom)} – {fmtDay(busyExperiment.dateTo)}
                </Typography>
              )}
            </Box>
          )}
          {history && (
            <>
              <MonthlyUnitsBars
                months={history.months}
                series={hoveredUnits}
                days={hoveredDays}
                snapshotDays={history.snapshotDays}
                max={scaleMax}
                locale={locale}
              />
              {hasAbsentMonth(hoveredUnits, hoveredDays) && (
                <Typography sx={{ fontSize: '10px', color: colors.text.muted, mt: 0.25 }}>{t('productMachines_laneLegend')}</Typography>
              )}
            </>
          )}
        </Box>
      </Popper>

      <Box>
        {suggestion ? (
          <ExperimentSuggestion
            key={suggestion.experiment.id}
            clientId={clientId}
            machineId={machineId}
            experiment={suggestion.experiment}
            lanes={suggestion.lanes}
            allProducts={allProducts}
            locale={locale}
            onClose={onCloseSuggestion}
            onResolved={onSuggestionResolved}
          />
        ) : laneCount === 0 ? (
          <Typography sx={{ fontSize: '11.5px', color: colors.text.secondary }}>{t('exp_queueHint')}</Typography>
        ) : (
          <>
            {/* The queue: what goes out */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {groups.map((g) => (
                <Box key={g.productId} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5, pl: 1, pr: 0.25, py: '2px', maxWidth: '100%',
                  borderRadius: '12px', backgroundColor: '#eff6ff', border: `1px solid ${colors.primary.main}40`,
                }}>
                  <Typography noWrap sx={{ fontSize: '11px', color: colors.text.primary, minWidth: 0 }}>
                    {shortName(g.name)} <Box component="span" sx={{ color: colors.text.secondary }}>· {g.slots.map((s) => s.selection_id).join(', ')}</Box>
                  </Typography>
                  <IconButton size="small" aria-label={t('exp_remove')} disabled={creating} onClick={() => onRemoveLanes(g.slots)} sx={{ p: '1px' }}>
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>

            {!picking ? (
              <Button
                fullWidth size="small" variant="contained" disableElevation onClick={onStartPicking}
                sx={{ mt: 1.25, textTransform: 'none', fontSize: '12px', borderRadius: '8px' }}
              >
                {t('exp_pickReplacement', { count: laneCount })}
              </Button>
            ) : (
              <>
                {/* What comes in: a click on the planogram, or any product here; said so when nothing is recommended */}
                <Typography sx={{ fontSize: '11px', color: colors.text.secondary, mt: 1.25, mb: 0.5 }}>
                  {loadingSuggestions ? <CircularProgress size={10} sx={{ mr: 0.5 }} /> : null}
                  {t(laneCandidates?.length === 0 && !loadingSuggestions && !recommended.length ? 'exp_pickHintEmpty' : 'exp_pickHint')}
                </Typography>
                {/* The best products for these lanes and what they would add */}
                <LaneCandidateList
                  candidates={laneCandidates}
                  originLaneCapacities={originLaneCapacities}
                  originName={originName}
                  chosenId={incoming?.option?.id}
                  disabled={creating}
                  onPick={(c) => onIncomingChange(Number(incoming?.option?.id) === c.productId
                    ? null
                    : { option: { id: c.productId, name: c.name, group: 'all' }, laneKey: null })}
                />
                <IncomingProductAutocomplete
                  value={previewIncoming ?? incoming?.option ?? null}
                  previewing={!!previewIncoming}
                  alternatives={recommended}
                  allProducts={allProducts}
                  excludeId={groups.length === 1 ? groups[0].productId : null}
                  disabled={creating}
                  placeholder={t('machines_searchProduct')}
                  onChange={(v) => onIncomingChange(v ? { option: v, laneKey: null } : null)}
                />
                <ExperimentStartFields
                  startDate={startDate}
                  onStartDateChange={setStartDate}
                  weeks={weeks}
                  onWeeksChange={setWeeks}
                  endDate={endDate}
                  locale={locale}
                />
                {message && <Typography sx={{ fontSize: '11px', color: '#b45309', mt: 0.75 }}>{message}</Typography>}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 1.25 }}>
                  <Button size="small" onClick={onBack} disabled={creating} sx={{ textTransform: 'none', fontSize: '12px', color: colors.text.secondary }}>
                    {t('exp_back')}
                  </Button>
                  <Button
                    size="small" variant="contained" disableElevation onClick={create}
                    disabled={!incoming || creating || startsTooSoonForErp(startDate)}
                    startIcon={creating ? <CircularProgress size={12} color="inherit" /> : null}
                    sx={{ textTransform: 'none', fontSize: '12px', borderRadius: '8px', backgroundColor: '#2f2a3d', '&:hover': { backgroundColor: '#1f1b2b' } }}
                  >
                    {t('productMachines_create', { count: groups.filter((g) => g.productId !== Number(incoming?.option?.id)).length })}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ExperimentModePanel;
